<script setup lang="ts">
import { trackSkipOnboarding, trackOngoingOnboarding, trackStartsTour } from '~/tracking/onboardingEvents.js'
import { useResultStore } from '~/store/result'
import { ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries'

defineOptions({
	name: 'ModalWelcome',
})

const { $config } = useNuxtApp()

const RAW_CDN = ($config.public.cdn || '').trim()
const ABS_CDN = /^https?:\/\//i.test(RAW_CDN)
const CDN_BASE = RAW_CDN.replace(/\/+$/, '')

/* Helper para construir rutas de imagen SIEMPRE coherentes */
const asset = (p?: string) => {
	if (!p) return ''
	const clean = p.replace(/^\/+/, '')
	if (ABS_CDN && CDN_BASE) return `${CDN_BASE}/${clean}`
	return `/cdn/${clean}`
}

useHead({
	link:
		ABS_CDN && CDN_BASE
			? [
					{ rel: 'preconnect', href: CDN_BASE, crossorigin: 'anonymous' },
					{ rel: 'dns-prefetch', href: CDN_BASE },
				]
			: [],
})

const emit = defineEmits(['update:modelValue', 'startTour'])

const resultStore = useResultStore()
const { t: $t, locale } = useI18n()

interface Props {
	modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
})

const getLotteryName = computed(() => {
	if (resultStore.getLotterySelect === null) return $t('EuroMillions')
	return resultStore.getLotterySelect?.id === ID_EURO_MILLIONS
		? $t(resultStore.getLotterySelect?.name)
		: resultStore.getLotterySelect?.name
})

const steps = computed(() => {
	const lotteryName = getLotteryName.value
	const step_1_description = $t(`We've analyzed the history of your numbers in the {lottery}! {question}`, {
		lottery: lotteryName,
		question: `<span class="font-bold">${$t(`Let's see how they performed in the past`)}?</span> ✨`,
	})

	const step_2_description = $t(`{text} (for the past!). We show how your numbers performed in previous draws`, {
		text: `<span class="font-bold">${$t(`We're like a time machine?`)}</span> ✨`,
	})

	const step_3_description = $t(`But that's not all! Discover {text} from various lotteries.`, {
		text: `<span class="font-bold">${$t('detailed analyses and check past results?')}</span> ✨`,
	})

	return [
		{
			id: 1,
			title: $t('Welcome!'),
			description: step_1_description,
			en: {
				image_desktop: 'onboarding_step_first_desktop.webp',
				image_mobile: 'onboarding_step_first_mobile.webp',
			},
			fr: {
				image_desktop: 'onboarding_step_first_desktop_fr.webp',
				image_mobile: 'onboarding_step_first_mobile_fr.webp',
			},
			pt: {
				image_desktop: 'onboarding_step_first_desktop_pt.webp',
				image_mobile: 'onboarding_step_first_mobile_pt.webp',
			},
		},
		{
			id: 2,
			title: $t('How Our Analysis Works'),
			description: step_2_description,
			en: {
				image_desktop: 'onboarding_step_second_desktop_img.webp',
				image_mobile: 'onboarding_step_second_mobile.webp',
			},
			fr: {
				image_desktop: 'onboarding_step_second_desktop_fr.webp',
				image_mobile: 'onboarding_step_second_mobile_fr.webp',
			},
			pt: {
				image_desktop: 'onboarding_step_second_desktop_pt.webp',
				image_mobile: 'onboarding_step_second_mobile_pt.webp',
			},
		},
		{
			id: 3,
			title: $t('Explore the Full Potential'),
			description: step_3_description,
			en: {
				image_desktop: 'onboarding_step_third_desktop_img.webp',
				image_mobile: 'onboarding_step_third_mobile.webp',
			},
			fr: {
				image_desktop: 'onboarding_step_third_desktop_fr.webp',
				image_mobile: 'onboarding_step_third_mobile_fr.webp',
			},
			pt: {
				image_desktop: 'onboarding_step_third_desktop_pt.webp',
				image_mobile: 'onboarding_step_third_mobile_pt.webp',
			},
		},
	]
})

const currentStep = ref(0)

const isOpen = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})

const getStep = computed(() => {
	return steps.value[currentStep.value] || steps.value[0]
})

const initTour = () => {
	if (resultStore.lotteries === null) {
		resetPropertys()
		return
	}
	trackStartsTour({
		game_name: resultStore.getLotterySelect?.name || '',
		step_id: currentStep.value + 1,
	})
	startTour()
}

const nextStep = async () => {
	if (currentStep.value === steps.value.length - 1) {
		initTour()
		return
	}

	trackOngoingOnboarding({
		game_name: resultStore.getLotterySelect?.name || '',
		step_id: currentStep.value + 1,
	})
	currentStep.value++
}

const prevStep = () => {
	if (currentStep.value <= 0) return
	currentStep.value--
}

const resetPropertys = () => {
	isOpen.value = false
	currentStep.value = 0
}

const startTour = () => {
	resetPropertys()
	emit('startTour')
}

const closeModal = () => {
	trackSkipOnboarding({
		game_name: resultStore.getLotterySelect?.name || '',
		step_id: currentStep.value + 1,
	})
	startTour()
}

onMounted(() => {
	const first = steps.value[0]
	new Image().src = asset(first[locale.value]?.image_desktop)
	new Image().src = asset(first[locale.value]?.image_mobile)
})

const prefetch = (src: string) => {
	const img = new Image()
	img.decoding = 'async'
	img.src = src
}

watch(currentStep, (step) => {
	const next = steps.value[step + 1]
	if (!next) return
	prefetch(asset(next[locale.value]?.image_desktop))
	prefetch(asset(next[locale.value]?.image_mobile))
})

watch(isOpen, (open) => {
	if (!open) return
	for (const s of steps.value) {
		const desktop = (s as any)?.[locale.value]?.image_desktop || (s as any).image_desktop
		const mobile = (s as any)?.[locale.value]?.image_mobile || (s as any).image_mobile

		if (desktop) prefetch(asset(desktop))
		if (mobile) prefetch(asset(mobile))
	}
})
</script>

<template>
	<ClientOnly>
		<UModal
			v-model="isOpen"
			:transition="false"
			:ui="{
				overlay: {
					background: 'bg-[#040404D4] bg-opacity-80',
				},
				width: 'w-full sm:max-w-4xl',
				container: 'items-center',
				padding: 'p-4 sm:p-4',
			}"
			class="max-h-[450px]"
		>
			<UCard
				:ui="{
					ring: '',
					divide: 'divide-y divide-gray-100 dark:divide-gray-800',
					body: { padding: 'p-6' },
				}"
			>
				<div class="flex justify-between items-center text-[#212529]">
					<div class="flex items-center gap-1">
						<UButton
							v-if="currentStep"
							icon="i-material-symbols:arrow-back-rounded"
							variant="link"
							class="text-neutral-900 hover:no-underline hover:text-primary-400"
							@click="prevStep"
						/>
						<p class="text-sm">{{ currentStep + 1 }}/{{ steps.length }}</p>
					</div>

					<UIcon
						name="i-heroicons-x-mark-20-solid"
						class="w-8 h-8"
						@click="closeModal"
					/>
				</div>
				<div class="py-4">
					<div class="max-h-[305px] sm:max-h-[170px] w-full">
						<NuxtImg
							:src="asset(getStep && getStep[locale] && getStep[locale]?.image_desktop)"
							format="webp"
							width="829"
							height="158"
							densities="1x 2x"
							sizes="829px"
							:alt="getStep.title"
							:title="getStep.title"
							class="w-full max-h-full object-contain hidden sm:block"
						/>
						<NuxtImg
							:src="asset(getStep && getStep[locale] && getStep[locale]?.image_mobile)"
							format="webp"
							:alt="getStep.title"
							:title="getStep.title"
							class="w-full max-h-full object-contain block sm:hidden"
						/>
					</div>
				</div>

				<div class="space-y-5">
					<div class="sm:text-center text-base sm:text-lg space-y-5 pb-3">
						<h2 class="font-bold">{{ getStep.title }}</h2>
						<p
							class="text-neutral-800 max-w-xl mx-auto"
							v-html="getStep.description"
						></p>
					</div>
					<div class="flex justify-end gap-2">
						<UButton
							v-if="steps.length - 1 !== currentStep"
							color="white"
							variant="link"
							:label="$t('Skip Onboarding')"
							class="text-neutral-900 hover:no-underline hover:text-primary-400"
							@click="closeModal"
						/>
						<UButton
							color="primary"
							:label="$t('Got it')"
							:ui="{
								rounded: 'rounded-full',
							}"
							class="min-w-[120px] flex justify-center"
							@click="nextStep"
						/>
					</div>
				</div>
			</UCard>
		</UModal>
	</ClientOnly>
</template>
